import {MaybeArray} from '@coolcolduk/typescript-util';
import {castArray} from '@coolcolduk/util';
import {StandardSubject, SubjectLevel} from '@cp/model';
import {ExamModuleSelected} from './ExamModuleSelected';

/**
 *
 * @param isUserSelected - True if filter for selected module, false to filter unselected modules
 * @returns
 */
export function filterExamModuleSelectedByUserSelected(isUserSelected: boolean) {
  return (examModuleSelected: ExamModuleSelected) => (examModuleSelected.userModule !== undefined) === isUserSelected;
}

/**
 * Filter exam modules by subject
 * @param subject - Subject to filter by (empty array means no filter)
 * @returns
 */
export function filterExamModuleSelectedBySubject(subject: MaybeArray<StandardSubject>) {
  const subjects = castArray(subject);
  if (subjects.length === 0) {
    return () => true;
  }
  return (examModuleSelected: ExamModuleSelected) => subjects.includes(examModuleSelected.subject);
}

/**
 * Filter exam modules by level
 * @param level - Level to filter by (empty array means no filter)
 * @returns
 */
export function filterExamModuleSelectedByLevel(level: MaybeArray<SubjectLevel>) {
  const levels = castArray(level);
  if (levels.length === 0) {
    return () => true;
  }
  return (examModuleSelected: ExamModuleSelected) => levels.includes(examModuleSelected.level as SubjectLevel);
}

/**
 * Filter exam modules by exam board
 * @param examBoard - Exam board to filter by (empty array means no filter)
 * @returns
 */
export function filterExamModuleSelectedByExamBoard(examBoard: MaybeArray<string>) {
  const examBoards = castArray(examBoard);
  if (examBoards.length === 0) {
    return () => true;
  }
  return (examModuleSelected: ExamModuleSelected) => examBoards.includes(examModuleSelected.examBoard as string);
}

/**
 * Filter exam modules by search query
 * @param searchQuery - Search query to filter by (empty string means no filter)
 * @returns
 */
export function filterExamModuleSelectedBySearchQuery(searchQuery: string) {
  if (searchQuery.trim() === '') {
    return () => true;
  }
  return (examModuleSelected: ExamModuleSelected) => {
    const query = searchQuery.toLowerCase();
    return (
      examModuleSelected.name.toLowerCase().includes(query) ||
      examModuleSelected.subject?.toLowerCase().includes(query) ||
      examModuleSelected.level?.toLowerCase().includes(query) ||
      examModuleSelected.examBoard?.toLowerCase().includes(query)
    );
  };
}

/**
 * Filter exam modules by search query, subject, level, and exam board
 * @param searchQuery - Search query to filter by
 * @param subject - Subject to filter by
 * @param level - Level to filter by
 * @param examBoard - Exam board to filter by
 * @returns
 */
export function filterExamModuleSelectedByAll(
  searchQuery: string,
  subject: MaybeArray<StandardSubject>,
  level: MaybeArray<SubjectLevel>,
  examBoard: MaybeArray<string>,
) {
  return (examModuleSelected: ExamModuleSelected) =>
    filterExamModuleSelectedByUserSelected(false)(examModuleSelected) &&
    filterExamModuleSelectedBySearchQuery(searchQuery)(examModuleSelected) &&
    filterExamModuleSelectedBySubject(subject)(examModuleSelected) &&
    filterExamModuleSelectedByLevel(level)(examModuleSelected) &&
    filterExamModuleSelectedByExamBoard(examBoard)(examModuleSelected);
}
